<div class="container">
  <div class="margin">
    <mat-stepper #stepper class="steps" linear="true">
      <!--first step-->
      <mat-step [stepControl]="firstFormGroup" errorMessage="Name is required.">
        <form [formGroup]="firstFormGroup" class="first-form">
          <ng-template matStepLabel>Account Creation</ng-template>
          <div class="info">
            <div class="title-large">Create AWS Cloud Account</div>
            <div class="subtitle">Provide the details for your AWS account.</div>
            <mat-error *ngIf="firstFormGroup.invalid && firstFormGroup.dirty" class="error">
              You must fill out all of the required fields to complete this form.
            </mat-error>
          </div>

          <div class="warning-banner">
            <img src="../../assets/icons/alert-circle.svg" />
            <p>
              Before requesting an AWS account, all must attend & receive approval from the
              <a href='https://generalmotors.sharepoint.com/:u:/r/sites/etap/SitePages/Cloud-Governance-Council.aspx?csf=1&web=1&e=DskgT3'
                target="_blank" class="warning-banner-link">
                Cloud Governance Council Meeting </a> (Every Monday).
            </p>
          </div>

          <div class="form">
            <div class="form-section" id="account-details">
              <p class="title-small">
                Account Details
                <img src="../../assets/icons/alert-circle.svg" *ngIf="noAsms | async"
                  matTooltip="You do not own an ASMS.  Return and refresh the page once you've made one."
                  matTooltipPosition="after" matTooltipClass="warning-tooltip" />
              </p>
              <div class="form-row">
                <p class="subtitle">
                  Select the ASMS to associate with your AWS account.
                </p>

                <div [ngClass]="{ 'has-spinner': isAsmsLoading }">
                  <div class="spinner-container">
                    <div class="spinner" *ngIf="isAsmsLoading">
                      <mat-spinner [diameter]="20" [strokeWidth]="2" />
                    </div>
                    <div class="form-input"><mat-form-field appearance="outline">
                        <!-- <div class = "form-input"><mat-form-field appearance="outline" (focusout)="isAsmsOccupied(firstFormGroup)"> -->
                        <mat-label>ASMS</mat-label>
                        <mat-select formControlName="asms" required>
                          <mat-option *ngFor="let item of asmsList" [value]="item.asmsId">
                            {{ item.asmsId }}
                          </mat-option>
                        </mat-select>
                      </mat-form-field></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="approval-screenshot form-section">
              <p class="title-small">CGC Approval Screenshot</p>
              <div class="form-row">
                <p class="subtitle">
                  Upload a screenshot of the approved request from the CGC meeting.
                </p>
                <div class="form-input">
                  <div class="file-upload">
                    <button mat-raised-button (click)="fileInput.click()" class="button-link button-upload">
                      Upload Screenshot
                    </button>
                    <input type="file" #fileInput (change)="onFileSelected($event)" accept="image/*"
                      style="display: none" formControlName="screenshot" />
                    <span class="file-name">
                      {{ fileName || 'No screenshot uploaded' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="suffix form-section">
              <p class="title-small">Account Name Suffix</p>
              <div class="form-row">
                <p class="subtitle">
                  Enter an alphanumeric suffix for the AWS account name (max 8 characters). Optional, defaults to
                  "main".
                </p>

                <div class="form-input suffix-input">
                  <span>{{buildAwsAccountNamePrefix()}}</span>

                  <mat-form-field appearance="outline" class="suffix-field">
                    <mat-label>Suffix</mat-label>

                    <input matInput placeholder="Ex. main" formControlName="suffix">

                    <mat-error *ngIf="firstFormGroup.get('suffix')?.hasError('pattern')">
                      Input must be alphanumeric.
                    </mat-error>
                    <mat-error *ngIf="firstFormGroup.get('suffix')?.hasError('maxlength')">
                      Input cannot exceed 8 characters.
                    </mat-error>
                    <mat-error *ngIf="firstFormGroup.get('suffix')?.hasError('notUnique')">
                      Account name exists. Pick new suffix.
                    </mat-error>
                    <mat-error *ngIf="firstFormGroup.get('suffix')?.hasError('apiError')">
                      Error checking account name. Refresh and try again.
                    </mat-error>
                  </mat-form-field>
                </div>

              </div>

            </div>

            <div class="environment form-section">
              <p class="title-small">Environment</p>
              <div class="form-row">
                <p class="subtitle">
                  Choose the development environment to associate the account with.
                </p>
                <div [ngClass]="{ 'has-spinner': populatingEnv }">
                  <div class="spinner-container">
                    <div class="spinner" *ngIf="populatingEnv">
                      <mat-spinner [diameter]="20" [strokeWidth]="2" />
                    </div>
                    <div class="form-input">
                      <mat-form-field appearance="outline">
                        <mat-label>Environment</mat-label>
                        <mat-select formControlName="environment" required>
                          @for (env of env; track env) {
                          <mat-option [value]="env.value">{{ env.viewValue }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      <a mat-button class="button-link"
                        href="https://colibri.gm.com/documents/azure-cloud/gm-platform/subscription-model#environments"
                        target="_blank" rel="noopener noreferrer">
                        <div class="button-link-content">
                          Learn More
                          <div class="button-image"> <img src="../../assets/images/external-link.svg" /> </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="region form-section">
              <p class="title-small">Region</p>
              <div class="form-row">
                <p class="subtitle">
                  Displayed is the region that your account will be created in. Note: US-East-2 is the default option.
                </p>
                <div class="form-input">
                  <mat-form-field appearance="outline">
                    <mat-label>Region</mat-label>
                    <mat-select formControlName="region" required>
                      @for (region of region; track region) {
                      <mat-option [value]="region.value">{{ region.viewValue }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <a mat-button class="button-link"
                    href="https://colibri.gm.com/documents/azure-cloud/workload-architecture/region-selection"
                    target="_blank" rel="noopener noreferrer">
                    <div class="button">
                      Learn More
                      <div class="button-image"> <img src="../../assets/images/external-link.svg" /> </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <div class="network-model form-section">
              <p class="title-small">Network Model</p>

              <div class="form-row">
                <p class="subtitle">
                  Select the available Account network model for your account. Note: Online is the default option.
                </p>

                <div class="form-input">
                  <mat-form-field appearance="outline">
                    <mat-label>Network Model</mat-label>
                    <mat-select formControlName="orgUnit" required>
                      @for (org of orgUnit; track orgUnit) {
                      <mat-option [value]="org.value">
                        {{org.viewValue}}
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <a mat-button class="button-link"
                    href="https://colibri.gm.com/documents/azure-cloud/gm-platform/networking/subscription-network-models"
                    target="_blank" rel="noopener noreferrer">
                    <div class="button">
                      Learn More
                      <div class="button-image"> <img src="../../assets/images/external-link.svg" /> </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            <!--repos section-->
            <div class="form-section repos">

              <p class="title-small">GitHub Repos</p>

              <div class="form-row">
                <p class="subtitle">
                  Create a comma separated list of all Github repository names for application deployment credentials.
                  Up to 10
                  repos may be added.
                </p>

                <div class="form-row-right textarea-field">
                  <mat-form-field appearance="outline" class="textarea" style="width: 21vw;" >
                    <mat-label>Enter repo name(s)</mat-label>
                    <textarea matInput formControlName="repos"></textarea>
                    <mat-hint>Expected Repo Format: org name/repo name</mat-hint>
                  </mat-form-field>

                  <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('invalidOrg')">
                    Invalid organization name. Please make sure to use am approved organization.
                  </mat-error>
                  <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('tooManyRepos')">
                    You have entered too many repositories.
                  </mat-error>
                  <mat-error *ngIf="firstFormGroup.get('repos')?.hasError('invalidRepoFormat')">
                    Invalid repository format. Example of accepted format: organization/repository
                  </mat-error>
                </div>
              </div>

            </div>

          </div>

        </form>

        <div class="button-container">
          <button mat-flat-button matStepperNext class="button-form-nav button-cancel" (click)="cancel()">
            Cancel
          </button>
          <button mat-flat-button matStepperNext [disabled]="firstFormGroup.invalid || checkingAccountName "
            class="button-form-nav"
            [ngClass]=" firstFormGroup.invalid || checkingAccountName  ? 'button-disabled' : 'button-next'">
            Next
          </button>
        </div>
      </mat-step>

      <!--second step-->
      <mat-step [stepControl]="secondFormGroup" errorMessage="Address is required.">
        <form [formGroup]="secondFormGroup">
          <ng-template matStepLabel>Confirmation</ng-template>

          <div class="info">
            <div class="title-large">Confirm AWS Cloud Account Details</div>
            <div class="subtitle">Confirm the account details. Press ‘Previous’ to make any edits before confirming.
            </div>
          </div>
          <div class="confirmation-container">
            <div class="confirmation-content">
              <div>
                <p>Account Details</p>
                <p>
                  <span class="asms-id">ASMS ID </span> {{ firstFormGroup.get('asms')?.value || '######'}}
                </p>
              </div>

              <div>
                <p>Environment</p>
                <p>
                  {{ getFormControlViewValue('environment', firstFormGroup.get("environment")?.value || 'None Selected')
                  }}
                </p>
              </div>

              <div>
                <p>Account Name Suffix</p>
                <p class="confimrationAccountName">
                  {{buildAwsAccountNamePrefix()}}
                  <b>
                    {{ firstFormGroup.get("suffix")?.value || 'main (default)' }}
                  </b>
                </p>
              </div>

              <div>
                <p>Region</p>
                <p>
                  {{ getFormControlViewValue('region', firstFormGroup.get("region")?.value || 'None Selected') }}
                </p>
              </div>
              <div>
                <p>Network Model</p>
                <p>
                  {{ getFormControlViewValue('orgUnit', firstFormGroup.get("orgUnit")?.value || 'None Selected') }}
                </p>
              </div>
            </div>
            <div class="button-container">
              <button mat-flat-button class="button-form-nav button-cancel" (click)="cancel()">
                Cancel
              </button>
              <div class="buttons-right">
                <button mat-button matStepperPrevious class="button-form-nav button-previous">
                  Previous
                </button>
                <button mat-flat-button matStepperNext (click)="submit()" class="button-form-nav button-submit">
                  Create Account
                </button>
              </div>
            </div>
          </div>
        </form>
      </mat-step>
    </mat-stepper>
  </div>

</div>